/* eslint-disable max-len */
import {
  AccountConversationDataDetail,
  AccountConversationDataType,
  ChatBotInfoResponseTypes,
  ConversationResponseTypes,
  DataConversationTypes,
  FeedbackConversationService,
  AiAssistantServiceTypes,
  ChatMessageTypes,
  CreateAgentConversationParamsTypes,
  CreateConversationParamsTypes,
  ResponseTimePayload,
} from 'services/accountConversations/types';
import { AuthResTypes } from 'services/auth/types';
import axiosChatbotInstance from 'services/common/chatBotInstance';
import axiosInstance from 'services/common/instance';
import { getLocalStorage } from 'services/common/storage';
import { LOCAL_STORAGE_CHATBOT } from 'utils/constants';

export const verifyChatBotService = async ({
  integrateUrl,
  integrateCode,
}: {
  integrateUrl: string;
  integrateCode?: string;
}): Promise<AuthResTypes> => {
  const res = await axiosInstance.post('chat-bot/verify', {
    integrateUrl,
    integrateCode,
  });
  return res.data.data;
};

export const verifyTestChatBotService = async (): Promise<AuthResTypes> => {
  const res = await axiosInstance.post('chat-bot/agent/verify');
  return res.data.data;
};

// eslint-disable-next-line operator-linebreak
export const getChatBotInfoService =
  async (): Promise<ChatBotInfoResponseTypes> => {
    const res = await axiosChatbotInstance.get('chat-bot/info');
    return res.data.data;
  };

export const sendOTPChatBotService = async (params: {
  phone: string;
}): Promise<{
  otp: string;
}> => {
  const res = await axiosChatbotInstance.post(
    'chat-bot/account/send-otp',
    params
  );
  return res.data.data;
};

export const sendMailOTPChatBotService = async (params: {
  mail: string;
}): Promise<{
  otp: string;
}> => {
  const res = await axiosChatbotInstance.post(
    'chat-bot/account/send-mail-otp',
    params
  );
  return res.data.data;
};

export const verifyOTPChatBotService = async (params: {
  phone?: string;
  mail?: string;
  otp: string;
}): Promise<AuthResTypes> => {
  const res = await axiosChatbotInstance.post(
    'chat-bot/account/verify-otp',
    params
  );
  return res.data.data;
};

export const refreshSessionChatBotService = async (): Promise<AuthResTypes> => {
  const res = await axiosChatbotInstance.post(
    'chat-bot/account/chat-session-token',
    {
      refreshToken: getLocalStorage(LOCAL_STORAGE_CHATBOT.REFRESHTOKEN_CHATBOT),
    }
  );
  return res.data.data;
};

export const createConversationChatBotService = async (
  params: CreateConversationParamsTypes
): Promise<ConversationResponseTypes> => {
  const res = await axiosChatbotInstance.post('account-conversations', params);
  return res.data.data;
};

export const getConversationsChatBotService = async (
  code: string,
  params: RequestParamsTypes
): Promise<APIPaginationResponse<DataConversationTypes[]>> => {
  const res = await axiosChatbotInstance.get(
    `account-conversations/${code}/items`,
    { params }
  );
  return res.data;
};

export const getAccountConversationsService = async (
  params: RequestParamsTypes
): Promise<APIPagination<AccountConversationDataType[]>> => {
  const res = await axiosChatbotInstance.get('account-conversations', {
    params,
  });
  return res.data;
};

export const getAccountConversationsFavoriteService = async (
  params: RequestParamsTypes
): Promise<APIPagination<AccountConversationDataType[]>> => {
  const res = await axiosChatbotInstance.get('account-conversations/favorite/list', {
    params,
  });
  return res.data;
};

export const updateAccountConversationFavoriteService = async (
  { code, favorite }: { code: string, favorite: boolean }
): Promise<AuthResTypes> => {
  const res = await axiosChatbotInstance.put(`account-conversations/${code}/update-favorite`, { favorite });
  return res.data.data;
};

export const getAccountConversationByIdService = async (
  code: string
): Promise<AccountConversationDataDetail> => {
  const res = await axiosChatbotInstance.get(`account-conversations/${code}`);
  return res.data.data;
};

export const feedbackConversationService = async (
  conversationCode: string,
  code: string,
  params: FeedbackConversationService
): Promise<ConversationResponseTypes> => {
  const res = await axiosChatbotInstance.post(
    `account-conversations/${conversationCode}/items/${code}/feedback`,
    params
  );
  return res.data.data;
};

export const refreshTokenChatbotService = async (): Promise<AuthResTypes> => {
  const res = await axiosInstance.post('chat-bot/account/refresh-token', {
    refreshToken: getLocalStorage(LOCAL_STORAGE_CHATBOT.REFRESHTOKEN_CHATBOT),
  });
  return res.data.data;
};

export const deleteItemConversationChatBotService = async (params: {
  conversationCode: string;
  code: string;
}): Promise<AuthResTypes> => {
  const res = await axiosChatbotInstance.delete(
    `account-conversations/${params.conversationCode}/items/${params.code}`
  );
  return res.data;
};

export const deleteFileConversationChatBotService = async (
  code: string,
  params: {
    codes: string[];
  }
): Promise<void> => {
  const res = await axiosChatbotInstance.delete(
    `account-conversations/${code}/files`,
    { data: params }
  );
  return res.data;
};

export const getAiAssistantService = async (
  code: string
): Promise<AiAssistantServiceTypes[]> => {
  const res = await axiosChatbotInstance.get(
    `account-conversations/${code}/ai-assistant-services`
  );
  return res.data.data;
};

export const getTokenSocketService = async (): Promise<string> => {
  const res = await axiosChatbotInstance.post('websocket/chat/generate-token');
  return res.data.data?.token;
};

export const wsSendChatService = async (
  code: string,
  params: ChatMessageTypes
): Promise<any> => {
  const res = await axiosChatbotInstance.post(
    `account-conversations/${code}/ws-chat`,
    params
  );
  return res.data.data;
};

export const getTokenSocketTestAgentService = async (): Promise<string> => {
  const res = await axiosInstance.post('websocket/generate-token');
  return res.data.data?.token;
};

export const wsSendChatAgentService = async (
  code: string,
  params: ChatMessageTypes
): Promise<any> => {
  const res = await axiosInstance.post(
    `agents/${code}/conversations/${params.conversationCode}/chat-ws`,
    params
  );
  return res.data.data;
};

export const createAgentConversationService = async (
  params: CreateAgentConversationParamsTypes
): Promise<ConversationResponseTypes> => {
  const res = await axiosInstance.post(`agents/${params.code}/conversations`, {
    title: params.title,
  });
  return res.data.data;
};

export const getAgentAiAssistantService = async (
  code: string
): Promise<AiAssistantServiceTypes[]> => {
  const res = await axiosInstance.get(
    `agents/${code}/conversations/ai-assistant-services`
  );
  return res.data.data;
};

export const T2PService = async ({
  code,
  conversationCode,
}: {
  code: string;
  conversationCode: string;
}): Promise<{ link: string }> => {
  const res = await axiosChatbotInstance.post(
    `account-conversations/${conversationCode}/answer-speech/${code}`
  );
  return res.data.data;
};

export const saveResponseDiffService = async (params: ResponseTimePayload): Promise<ConversationResponseTypes> => {
  const res = await axiosChatbotInstance.post(`account-conversations/${params.codeConversation}/items/${params.code}/response-time`, { ttlResTime: params.ttlResTime });
  return res.data.data;
};

export const getVoiceTokenSocketService = async (): Promise<string> => {
  const res = await axiosInstance.post('websocket/voice/generate-token');
  return res.data.data?.token;
};
